import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import { ActiveCoverage } from '@lib/insurance-packages/data-access';
import { selectMemberActiveCoverage, selectMemberProfile } from '@lib/members/data-access';
import { MemberProfileConfig } from '@lib/members/feature/models';
import { FillViewportHeightDirective, PackageCoverageComponent } from '@lib/members/ui';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { calculateAge } from '@lib/commons/util-functions/calculate-age.function';
import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { DOBROBUT_PARTNER_ID, ExternalPatientReference, LILO_PARTNER_ID } from '@lib/core/models/member.interface';

import { ContractItemComponent } from '@lib/shared-ui/commons/contract-item/contract-item.component';
import { WarningMessageComponent } from '@lib/shared-ui/commons/warning-message/warning-message.component';

@Component({
  selector: 'members-profile',
  standalone: true,
  imports: [
    WarningMessageComponent,
    MatTooltip,
    NgOptimizedImage,
    RouterLink,
    ContractItemComponent,
    TranslatePipe,
    MatIcon,
    NgTemplateOutlet,
    PackageCoverageComponent,
    FillViewportHeightDirective,
  ],
  templateUrl: './member-profile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberProfileComponent {
  private readonly store = inject(Store);

  config = input<MemberProfileConfig>({
    showChatButton: true,
    showDoctorEdit: false,
    showPartnerIdEdit: false,
    showExternalRefs: false,
    showPackageCoverage: false,
  });

  protected readonly formatDateFunction = formatDate;
  protected readonly calculateAge = calculateAge;

  protected readonly memberProfile = this.store.selectSignal(selectMemberProfile);
  protected readonly showAllExternalRefs = signal<boolean>(false);

  private readonly activeCoverage = this.store.selectSignal(selectMemberActiveCoverage);
  protected readonly coverage = computed<ActiveCoverage | null>(() => {
    const coverage = this.activeCoverage().coverage;
    return coverage?.userId === this.memberProfile()?.userId ? (coverage.data ?? null) : null;
  });

  protected readonly liloPartnerId = LILO_PARTNER_ID;

  protected readonly externalRefs = computed((): ExternalPatientReference[] => {
    if (!this.config().showExternalRefs) return [];

    const result = [...(this.memberProfile()?.externalPatientReferences || [])];
    if (!result.some(r => r.partnerId === DOBROBUT_PARTNER_ID)) {
      result.push({ partnerId: DOBROBUT_PARTNER_ID, partnerName: 'Добробут', patientId: '' });
    }
    if (!result.some(r => r.partnerId === LILO_PARTNER_ID)) {
      result.push({ partnerId: LILO_PARTNER_ID, partnerName: 'lilo', patientId: '' });
    }
    return result.sort((a, b) => {
      if (a.partnerId === DOBROBUT_PARTNER_ID) return -1;
      if (b.partnerId === DOBROBUT_PARTNER_ID) return 1;
      if (a.partnerId === LILO_PARTNER_ID) return -1;
      if (b.partnerId === LILO_PARTNER_ID) return 1;
      return 0;
    });
  });

  protected findLiloRef(): ExternalPatientReference | undefined {
    return this.externalRefs().find(r => r.partnerId === LILO_PARTNER_ID);
  }

  protected async redirectToClientsChat(userId?: string) {
    if (this.config().onChatClick && userId) return this.config().onChatClick(userId);
  }

  protected openHelsiPatientIdDialog() {
    if (this.config().onHelsiIdEdit) this.config().onHelsiIdEdit();
  }

  protected openChangeDoctorDialog() {
    if (this.config().onDoctorEdit) this.config().onDoctorEdit();
  }

  protected toggleExternalRefsView() {
    this.showAllExternalRefs.set(!this.showAllExternalRefs());
  }
}
